/**
 * PATH: packages/tenant-shared/src/preferences/dbViewPreference.ts
 * FILE_ID: TENANTSHARED-DBVIEWPREF-CORE-001
 * PURPOSE: Preserve the original two-tier preference API for existing consumers.
 * ROLE IN ARCHITECTURE: Compatibility facade over quotechViewPreferenceAdapter.
 * MAIN EXPORTS: loadCachedState, fetchServerState, scheduleServerSave.
 * NON-RESPONSIBILITIES: Does not own transport, timers, revisions, or React.
 * NOTES FOR FUTURE AI: Migrate callers to the typed adapter without breaking scopes.
 */
import { viewPreferenceCacheKey } from '@quotech/api/view-preferences'

import {
  getQuotechViewPreferenceStore,
  quotechViewPreferenceCache,
  resolveViewPreferenceIdentity,
} from './quotechViewPreferenceAdapter'

export const VIEW_PREF_ENDPOINT = '/api/settings/view-preferences/'
export const DEFAULT_SAVE_DEBOUNCE_MS = 700
const LS_PREFIX = 'viewpref'
const COMPATIBILITY_SCHEMA_VERSION = 1

/** Validate an untrusted cache/server blob against caller-owned defaults. */
export type CoerceFn<T> = (raw: unknown, defaults: T) => T

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function shallowMerge<T extends object>(defaults: T, raw: unknown): T {
  if (!raw || typeof raw !== 'object') return clone(defaults)
  return { ...clone(defaults), ...(raw as Partial<T>) }
}

function coerceState<T extends object>(
  raw: unknown,
  defaults: T,
  coerce?: CoerceFn<T>,
): T {
  return coerce ? coerce(raw, defaults) : shallowMerge(defaults, raw)
}

function compatibilityStore<T extends object>(
  scopeKey: string,
  defaults: T,
  coerce?: CoerceFn<T>,
) {
  const identity = resolveViewPreferenceIdentity()
  return getQuotechViewPreferenceStore({
    identity,
    scopeKey,
    schemaVersion: COMPATIBILITY_SCHEMA_VERSION,
    defaults,
    coerce: (raw) => coerceState(raw, defaults, coerce),
    debounceMs: DEFAULT_SAVE_DEBOUNCE_MS,
  })
}

function migrateLegacyCache<T extends object>(
  scopeKey: string,
  defaults: T,
  coerce?: CoerceFn<T>,
): T | undefined {
  const legacyKey = `${LS_PREFIX}.${scopeKey}`
  const raw = quotechViewPreferenceCache.read(legacyKey)
  if (raw === undefined) return undefined
  const value = coerceState(raw, defaults, coerce)
  const identityKey = viewPreferenceCacheKey(resolveViewPreferenceIdentity(), scopeKey)
  quotechViewPreferenceCache.write(identityKey, value)
  quotechViewPreferenceCache.remove(legacyKey)
  return value
}

/**
 * Read a valid cached state synchronously without starting network work.
 *
 * Existing tenant-only cache data is consumed once into the safer user key.
 */
export function loadCachedState<T extends object>(
  scopeKey: string,
  defaults: T,
  coerce?: CoerceFn<T>,
): T {
  const migrated = migrateLegacyCache(scopeKey, defaults, coerce)
  const store = compatibilityStore(scopeKey, migrated ?? defaults, coerce)
  return clone(store.getSnapshot().value)
}

/** Replace only the local cache; scheduleServerSave owns durable sync. */
export function saveCachedState<T extends object>(scopeKey: string, value: T): void {
  compatibilityStore(scopeKey, value).cacheValue(value)
}

/** Hydrate the shared scope once; return null for missing or failed remote state. */
export async function fetchServerState<T extends object>(
  scopeKey: string,
  defaults: T,
  coerce?: CoerceFn<T>,
): Promise<T | null> {
  const store = compatibilityStore(scopeKey, defaults, coerce)
  await store.hydrate()
  const snapshot = store.getSnapshot()
  return snapshot.hasRemoteValue ? clone(snapshot.value) : null
}

/** Optimistically cache and debounce one full-blob revisioned PATCH. */
export function scheduleServerSave<T extends object>(
  scopeKey: string,
  value: T,
  debounceMs: number = DEFAULT_SAVE_DEBOUNCE_MS,
): void {
  compatibilityStore(scopeKey, value).setValue(value, { debounceMs })
}

/** Schedule a revisioned DELETE; callers retain their existing void contract. */
export async function resetServerState(scopeKey: string): Promise<void> {
  const store = compatibilityStore(scopeKey, {})
  store.reset({ debounceMs: 0 })
  await store.flush()
}

/** Clear only the current tenant/user/scope cache entry. */
export function clearCachedState(scopeKey: string): void {
  const key = viewPreferenceCacheKey(resolveViewPreferenceIdentity(), scopeKey)
  quotechViewPreferenceCache.remove(key)
}
